<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router';
import {
  AlertTriangle, BarChart3, Bell, Bot, BriefcaseBusiness, CalendarRange, CheckSquare2, ChevronDown, Handshake,
  ArchiveX, GitBranch, Inbox, KeyRound, Lightbulb, Menu, Network, ReceiptText, Search, Settings2, Sparkles, UserRound, UsersRound, X, CalendarClock,
  Bug, CarFront, Smartphone, CloudUpload,
  BookOpen, ClipboardCheck, Images, LogOut, Undo2,
} from 'lucide-vue-next';
import { activeDashboardUserId, apiGet, apiPost, authRequired, clearApiToken, getApiToken, impersonationActive, restoreAdministratorSession, revokeApiToken, setActiveDashboardUser, setApiToken, useLiveEvents } from './composables/api';
import { crmEnabled, debugMode, evidenceReaderSession, neo4jEnabled, setSessionAccess, simplifiedMode } from './composables/runtime-config';
import ApiImage from './components/ApiImage.vue';
import OfflineActionDialog from './components/OfflineActionDialog.vue';
import NativeOtaUpdateDialog from './components/NativeOtaUpdateDialog.vue';
import OfflineQueueRibbon from './components/OfflineQueueRibbon.vue';
import WebAppStatusBar from './components/WebAppStatusBar.vue';
import { startConnectivityTracking, stopConnectivityTracking } from './composables/connectivity';
import { offlineQueueDisplayCount, startOfflineQueue, stopOfflineQueue } from './composables/offline-queue';
import { useCompanyLogo } from './composables/company-logo';
import { setNativeStatusBarStyle } from './native-runtime';

const route = useRoute();
const router = useRouter();
const embeddedAccessToken = typeof route.query.access_token === 'string' ? route.query.access_token : '';
if (embeddedAccessToken) {
  setApiToken(embeddedAccessToken);
  const cleanQuery = { ...route.query };
  delete cleanQuery.access_token;
  void router.replace({ query: cleanQuery });
}
const sidebarOpen = ref(false);
const searchOpen = ref(false);
const searchQuery = ref('');
const searchResults = ref<any[]>([]);
const searching = ref(false);
const searchInput = ref<HTMLInputElement>();
const toast = ref('');
const loginEmail = ref('');
const loginCode = ref('');
const loginPassword = ref('');
const loginStep = ref<'email' | 'otp' | 'password'>('email');
const authBusy = ref(false);
const authError = ref('');
const authMessage = ref('');
const accessResolved = ref(false);
const { companyLogoUrl, loadCompanyLogo } = useCompanyLogo();
void loadCompanyLogo();
type WebAppPermission = 'survey' | 'evidence' | 'delivery_logs' | 'responsibilities';
type DashboardSessionUser = { id: string; email: string; name: string; role: string; accessRole: 'administrator'|'manager'|'collaborator'|'delivery_driver'|'sistemas'|'gerente'; driverBranch: 'veracruz'|'xalapa'|null; warehouseBranch: 'veracruz'|'xalapa'|null; pendingSurveysAvailable: boolean; deliverySchedulesAvailable: boolean; scopeType: string; accountCount: number | null; isAdmin: boolean; personId: string | null; photoUrl: string | null; webAppPermissions: WebAppPermission[] };
const cachedWebAppUser = readCachedWebAppSession();
const dashboardUsers = ref<DashboardSessionUser[]>(cachedWebAppUser ? [cachedWebAppUser] : []);
if (cachedWebAppUser) setSessionAccess(cachedWebAppUser.accessRole, cachedWebAppUser.warehouseBranch, cachedWebAppUser.pendingSurveysAvailable, cachedWebAppUser.email, cachedWebAppUser.deliverySchedulesAvailable);
const profileMenuOpen = ref(false);
const navigationCounts = ref({ insights: 0, responsibilities: 0, promises: 0, risks: 0 });
const { connected } = useLiveEvents((event) => {
  const type = String(event.type ?? '');
  if (['risk.changed','insight.updated','responsibility.created','responsibility.updated','analysis.completed'].includes(type)) void loadNavigationCounts();
  if (type === 'risk.changed' || type === 'checkpoint.completed') {
    toast.value = type === 'risk.changed' ? 'Cambió el riesgo de una responsabilidad' : 'Checkpoint actualizado';
    window.setTimeout(() => { toast.value = ''; }, 3500);
  }
});

const activeDashboardUser = computed(() => dashboardUsers.value.find((user) => user.id === activeDashboardUserId.value) ?? dashboardUsers.value[0]);
const administrator = computed(() => activeDashboardUser.value?.accessRole === 'administrator');
const primaryNav = computed(() => simplifiedMode.value && !administrator.value ? [
  { to: '/report', label: 'Reporte diario', icon: BarChart3 },
  { to: '/panorama', label: 'Panorama', icon: CalendarClock },
  { to: '/surveys', label: 'Encuestas', icon: ClipboardCheck },
  ...(evidenceReaderSession.value ? [{ to: '/evidences', label: 'Evidencias', icon: Images }] : []),
  ...(crmEnabled.value ? [{ to: '/crm', label: 'CRM', icon: UsersRound }] : []),
] : !administrator.value ? [
  { to: '/report', label: 'Reporte diario', icon: BarChart3 },
  ...(crmEnabled.value ? [{ to: '/crm', label: 'CRM', icon: UsersRound }] : []),
  { to: '/panorama', label: 'Panorama', icon: CalendarClock },
  { to: '/surveys', label: 'Encuestas', icon: ClipboardCheck },
  ...(evidenceReaderSession.value ? [{ to: '/evidences', label: 'Evidencias', icon: Images }] : []),
  { to: '/emails', label: 'Correos', icon: Inbox },
  { to: '/threads', label: 'Hilos', icon: GitBranch },
] : [
  { to: '/report', label: 'Reporte diario', icon: BarChart3 },
  { to: '/panorama', label: 'Panorama', icon: CalendarClock },
  { to: '/insights', label: 'Insights', icon: Lightbulb, badge: navigationCounts.value.insights || undefined },
  { to: '/emails', label: 'Correos', icon: Inbox },
  { to: '/threads', label: 'Hilos', icon: GitBranch },
  ...(debugMode.value ? [{ to: '/debug', label: 'Debug', icon: Bug }] : []),
]);
const excludedMailNav = [
  { to: '/invoices-emails', label: 'Correos de Facturas', icon: ReceiptText },
  { to: '/services-access', label: 'Servicios y accesos', icon: KeyRound },
  { to: '/irrelevant-emails', label: 'Irrelevantes', icon: ArchiveX },
];
const workNav = computed(() => [
  ...(crmEnabled.value ? [{ to: '/crm', label: 'CRM', icon: UsersRound }] : []),
  { to: '/invoices', label: 'Facturas, Remisiones y Pedidos', icon: ReceiptText },
  { to: '/surveys', label: 'Encuestas', icon: ClipboardCheck },
  { to: '/evidences', label: 'Evidencias', icon: Images },
  { to: '/projects', label: 'Proyectos', icon: BriefcaseBusiness },
  { to: '/responsibilities', label: 'Responsabilidades', icon: CheckSquare2, badge: navigationCounts.value.responsibilities || undefined },
  { to: '/promises', label: 'Promesas', icon: Handshake, badge: navigationCounts.value.promises || undefined },
  { to: '/risks', label: 'Riesgos', icon: AlertTriangle, badge: navigationCounts.value.risks || undefined },
]);
const intelligenceNav = computed(() => [
  ...(neo4jEnabled.value ? [{ to: '/graph', label: 'Grafo operativo', icon: Network }] : []),
  { to: '/gantt', label: 'Cronograma', icon: CalendarRange },
  { to: '/checkpoints', label: 'Checkpoints', icon: BarChart3 },
  { to: '/ask', label: 'Consulta IA', icon: Bot },
]);
const directoryNav = [
  { to: '/customers', label: 'Clientes', icon: UsersRound },
  { to: '/people', label: 'Personas', icon: UserRound },
  { to: '/vehicles', label: 'Vehículos', icon: CarFront },
];
const channelNav = [
  { to: '/notifications', label: 'Notificaciones', icon: Bell },
  { to: '/tutoriales', label: 'Tutoriales', icon: BookOpen },
];
const adminChannelNav = [
  { to: '/devices', label: 'Dispositivos', icon: Smartphone },
  { to: '/ota', label: 'Actualizaciones OTA', icon: CloudUpload },
];
const pageTitle = computed(() => String(route.meta.title ?? 'Reporte diario'));
const webAppIcon = computed<'survey' | 'evidence' | 'log' | 'pending'>(() => {
  if (route.name === 'public-evidence' || route.name === 'evidences') return 'evidence';
  if (route.name === 'delivery-logs') return 'log';
  if (route.name === 'pending-responsibilities') return 'pending';
  return 'survey';
});
const publicPage = computed(() => Boolean(route.meta.public));
const protectedWebAppPage = computed(() => Boolean(route.meta.requiresWebAppAuth)
  || route.name === 'public-survey' && route.query.section === 'pending');
const launchedFromWebapp = computed(() => Boolean(route.meta.webAppChild) && route.query.app === '1');
const activeUserInitials = computed(() => (activeDashboardUser.value?.name ?? 'Usuario').split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase());
function accessRoleName(role?: string) { return role === 'administrator' ? 'Administrador' : role === 'sistemas' ? 'Sistemas' : role === 'manager' ? 'Manager' : role === 'gerente' ? 'Gerente' : role === 'delivery_driver' ? 'Repartidor' : 'Colaborador'; }

watch(searchQuery, async (query) => {
  if (query.trim().length < 2) { searchResults.value = []; return; }
  searching.value = true;
  try { searchResults.value = (await apiGet<any[]>(`/api/search?q=${encodeURIComponent(query.trim())}&limit=8`)).data; }
  catch { searchResults.value = []; }
  finally { searching.value = false; }
});

function openSearch() {
  searchOpen.value = true;
  void nextTick(() => searchInput.value?.focus());
}
function closeSearch() { searchOpen.value = false; searchQuery.value = ''; }
function onKeyboard(event: KeyboardEvent) {
  if (administrator.value && (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); openSearch(); }
  if (event.key === 'Escape') closeSearch();
}
function goResult(result: any) {
  closeSearch();
  if (result.resultType === 'email' || result.senderEmail) void router.push('/emails');
  else if (result.resultType === 'promise' || result.category === 'promise') void router.push('/promises');
  else if (result.resultType === 'responsibility' || result.responsibilitySide) void router.push('/responsibilities');
  else if (result.resultType === 'project') void router.push(`/projects/${encodeURIComponent(result.id)}`);
  else if (result.status && result.customer) void router.push('/projects');
  else void router.push('/insights');
}
async function requestLoginCode() {
  if (!loginEmail.value.trim() || authBusy.value) return;
  authBusy.value = true;
  authError.value = '';
  authMessage.value = '';
  try {
    const authBase = protectedWebAppPage.value ? '/api/webapp/auth' : '/api/auth';
    const result = await apiPost<{ method: 'password' } | { method: 'otp'; sent: true; expiresInMinutes: number }>(`${authBase}/request-code`, { email: loginEmail.value.trim() });
    loginStep.value = result.data.method;
    authMessage.value = result.data.method === 'otp'
      ? `Si el correo está registrado, recibirás un código válido por ${result.data.expiresInMinutes} minutos.`
      : '';
    await nextTick();
    document.querySelector<HTMLInputElement>('.auth-credential-input')?.focus();
  } catch (cause) {
    authError.value = (cause as Error).message;
  } finally {
    authBusy.value = false;
  }
}
function completeLogin(result: { data: { accessToken: string; expiresAt: string; user: { id: string } } }) {
  setApiToken(result.data.accessToken, result.data.expiresAt);
  setActiveDashboardUser(result.data.user.id);
  window.location.reload();
}
async function verifyLoginCode() {
  if (!/^\d{6}$/.test(loginCode.value) || authBusy.value) return;
  authBusy.value = true;
  authError.value = '';
  try {
    const authBase = protectedWebAppPage.value ? '/api/webapp/auth' : '/api/auth';
    const result = await apiPost<{ accessToken: string; expiresAt: string; user: { id: string } }>(`${authBase}/verify-code`, {
      email: loginEmail.value.trim(), code: loginCode.value,
    });
    completeLogin(result);
  } catch (cause) {
    authError.value = (cause as Error).message;
  } finally {
    authBusy.value = false;
  }
}
async function verifyPassword() {
  if (loginPassword.value.length < 8 || authBusy.value) return;
  authBusy.value = true;
  authError.value = '';
  try {
    const authBase = protectedWebAppPage.value ? '/api/webapp/auth' : '/api/auth';
    const result = await apiPost<{ accessToken: string; expiresAt: string; user: { id: string } }>(`${authBase}/verify-password`, {
      email: loginEmail.value.trim(), password: loginPassword.value,
    });
    completeLogin(result);
  } catch (cause) {
    authError.value = (cause as Error).message;
  } finally {
    authBusy.value = false;
  }
}
function resetLogin() {
  loginStep.value = 'email';
  loginCode.value = '';
  loginPassword.value = '';
  authError.value = '';
  authMessage.value = '';
}
async function logout() {
  try { await apiPost('/api/auth/logout', {}); } catch { /* La sesión local también debe cerrarse si el servidor no responde. */ }
  clearApiToken();
  window.location.reload();
}
async function returnToAdministrator() {
  await revokeApiToken(getApiToken()).catch(() => undefined);
  restoreAdministratorSession();
  window.location.assign('/people');
}
async function loadDashboardUsers() {
  try {
    const user = (await apiGet<(typeof dashboardUsers.value)[number]>('/api/auth/me')).data;
    dashboardUsers.value = [user];
    setActiveDashboardUser(user.id);
    setSessionAccess(user.accessRole, user.warehouseBranch, user.pendingSurveysAvailable, user.email, user.deliverySchedulesAvailable);
    enforceAccountRoute(user);
  } catch {
    if (!dashboardUsers.value.length) setSessionAccess(null);
    /* La copia local conserva el acceso a las vistas embebidas cuando no hay red. */
  }
}
function readCachedWebAppSession(): DashboardSessionUser | null {
  const keys = [activeDashboardUserId.value, 'session'].filter(Boolean)
    .map((id) => `eqover-webapp-user-v1:${id}`);
  for (const key of keys) {
    try {
      const value = JSON.parse(localStorage.getItem(key) ?? 'null') as Partial<DashboardSessionUser> | null;
      if (!value || typeof value.name !== 'string' || !Array.isArray(value.webAppPermissions)) continue;
      const accessRole = ['administrator', 'manager', 'collaborator', 'delivery_driver', 'sistemas', 'gerente'].includes(String(value.accessRole))
        ? value.accessRole as DashboardSessionUser['accessRole']
        : 'collaborator';
      return {
        id: typeof value.id === 'string' ? value.id : activeDashboardUserId.value || 'offline-session',
        email: typeof value.email === 'string' ? value.email : '',
        name: value.name,
        role: typeof value.role === 'string' ? value.role : '',
        accessRole,
        driverBranch: value.driverBranch === 'veracruz' || value.driverBranch === 'xalapa' ? value.driverBranch : null,
        warehouseBranch: value.warehouseBranch === 'veracruz' || value.warehouseBranch === 'xalapa' ? value.warehouseBranch : null,
        pendingSurveysAvailable: value.pendingSurveysAvailable === true || accessRole === 'administrator' || accessRole === 'manager' || accessRole === 'gerente',
        deliverySchedulesAvailable: value.deliverySchedulesAvailable === true || ['administrator', 'sistemas', 'manager', 'gerente'].includes(accessRole),
        scopeType: typeof value.scopeType === 'string' ? value.scopeType : 'email_accounts',
        accountCount: typeof value.accountCount === 'number' ? value.accountCount : null,
        isAdmin: accessRole === 'administrator',
        personId: typeof value.personId === 'string' ? value.personId : null,
        photoUrl: typeof value.photoUrl === 'string' ? value.photoUrl : null,
        webAppPermissions: value.webAppPermissions.filter((permission): permission is WebAppPermission =>
          ['survey', 'evidence', 'delivery_logs', 'responsibilities'].includes(permission)),
      };
    } catch { /* Prueba la siguiente copia local. */ }
  }
  return null;
}
function enforceAccountRoute(user = activeDashboardUser.value) {
  if (!user) return;
  if (publicPage.value) return;
  if (user.accessRole === 'administrator') return;
  const allowed = ['daily-report', 'panorama', 'surveys', 'emails', 'threads', 'invoices-emails', 'services-access', 'irrelevant-emails', 'notifications', 'responsibility-detail', 'tutorials', 'responsibilities-tutorial', 'surveys-tutorial', 'evidences-tutorial', 'delivery-log-tutorial', 'pending-responsibilities-tutorial', 'crm'];
  if (user.accessRole === 'sistemas' || user.accessRole === 'gerente' || user.accessRole === 'collaborator' || user.warehouseBranch) allowed.push('evidences');
  const ownProfile = route.name === 'person-detail' && String(route.params.id ?? '') === user.personId;
  if (!allowed.includes(String(route.name ?? '')) && !ownProfile) void router.replace('/report');
}
function setProductIdentity(visible: boolean) {
  document.title = visible ? 'Eqover · Inteligencia operativa' : 'Eqover';
  document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute(
    'content',
    visible ? 'Inteligencia operativa trazable a partir del correo de tu empresa.' : 'Inicia sesión en Eqover.',
  );
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute(
    'content',
    offlineQueueDisplayCount.value ? '#ffd9de' : visible ? '#10172a' : '#ffffff',
  );
}
function setPublicIdentity() {
  document.title = `Eqover · ${pageTitle.value}`;
  document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute(
    'content',
    String(route.meta.description ?? pageTitle.value),
  );
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute(
    'content',
    offlineQueueDisplayCount.value
      ? '#ffd9de'
      : launchedFromWebapp.value ? '#10172a' : String(route.meta.themeColor ?? '#f6f7f4'),
  );
}
async function initializeAccess() {
  if (dashboardUsers.value.length) {
    accessResolved.value = true;
    enforceAccountRoute();
    if (publicPage.value) setPublicIdentity();
  }
  await loadDashboardUsers();
  const requestedUser = typeof route.query.user === 'string' ? route.query.user : '';
  if (requestedUser && dashboardUsers.value.some((user) => user.id === requestedUser)) setActiveDashboardUser(requestedUser);
  accessResolved.value = true;
  if (authRequired.value) return;
  if (publicPage.value) setPublicIdentity();
  else {
    setProductIdentity(true);
    if (administrator.value) void loadNavigationCounts();
  }
}
async function loadNavigationCounts() {
  const [insights, workItems] = await Promise.allSettled([
    apiGet<any[]>('/api/insights?status=active&limit=500'),
    apiGet<{ responsibilities: number; promises: number; risks: number }>('/api/work-items/counts'),
  ]);
  navigationCounts.value = {
    insights: insights.status === 'fulfilled' ? insights.value.data.length : 0,
    responsibilities: workItems.status === 'fulfilled' ? workItems.value.data.responsibilities : 0,
    promises: workItems.status === 'fulfilled' ? workItems.value.data.promises : 0,
    risks: workItems.status === 'fulfilled' ? workItems.value.data.risks : 0,
  };
}
onMounted(() => {
  startConnectivityTracking();
  startOfflineQueue();
  window.addEventListener('keydown', onKeyboard);
  if (publicPage.value && !protectedWebAppPage.value) {
    accessResolved.value = true;
    setPublicIdentity();
  } else void initializeAccess();
});
onBeforeUnmount(() => {
  stopOfflineQueue();
  stopConnectivityTracking();
  window.removeEventListener('keydown', onKeyboard);
});
watch(authRequired, (required) => setProductIdentity(!required));
watch([launchedFromWebapp, offlineQueueDisplayCount], ([launched, offlineCount]) => {
  const offline = offlineCount > 0;
  void setNativeStatusBarStyle(launched && !offline ? 'light' : 'dark', offline ? '#ffd9de' : launched ? '#10172a' : '#ffffff');
  if (publicPage.value) setPublicIdentity();
  else setProductIdentity(!authRequired.value);
}, { immediate: true });
watch(() => route.fullPath, () => {
  enforceAccountRoute();
  if (publicPage.value) setPublicIdentity();
});
watch([publicPage, protectedWebAppPage], ([isPublic, isProtected]) => {
  if (isPublic && !isProtected) {
    accessResolved.value = true;
    setPublicIdentity();
  } else if (!dashboardUsers.value.length || authRequired.value) {
    accessResolved.value = false;
    void initializeAccess();
  }
});
</script>

<template>
  <div v-if="publicPage && (!protectedWebAppPage || (accessResolved && !authRequired))" :class="{ 'webapp-route-shell': launchedFromWebapp }">
    <OfflineQueueRibbon :sticky="launchedFromWebapp" />
    <WebAppStatusBar
      v-if="launchedFromWebapp"
      :title="pageTitle"
      :icon="webAppIcon"
      :respect-top-safe-area="offlineQueueDisplayCount === 0"
      :stacked-below-offline-ribbon="offlineQueueDisplayCount > 0"
    />
    <RouterView />
  </div>
  <div v-else-if="!accessResolved" class="access-blank" aria-hidden="true" />
  <div v-else-if="!authRequired" class="app-shell" :class="{ 'offline-pending': offlineQueueDisplayCount > 0 }">
    <OfflineQueueRibbon />
    <Transition name="fade"><div v-if="sidebarOpen" class="mobile-scrim" @click="sidebarOpen = false" /></Transition>
    <aside class="sidebar" :class="{ open: sidebarOpen }">
      <div class="brand">
        <div class="brand-mark"><span /><span /><span /></div>
        <div><strong>eqover</strong><small>intelligence</small></div>
        <button class="sidebar-close" aria-label="Cerrar menú" @click="sidebarOpen = false"><X :size="20" /></button>
      </div>
      <div class="company-switcher">
        <span class="company-avatar">EQ</span>
        <div><strong>Eqover</strong><small>{{ debugMode ? 'Modo debug' : 'Vista ejecutiva' }}</small></div>
        <ChevronDown :size="15" />
      </div>
      <nav class="nav-scroll">
        <p class="nav-label">{{ debugMode ? 'Diagnóstico' : 'Pulso' }}</p>
        <RouterLink v-for="item in primaryNav" :key="item.to" :to="item.to" @click="sidebarOpen = false">
          <component :is="item.icon" :size="18" /><span>{{ item.label }}</span><em v-if="item.badge">{{ item.badge }}</em>
        </RouterLink>
        <template v-if="administrator">
          <p class="nav-label">Correo excluido</p>
          <RouterLink v-for="item in excludedMailNav" :key="item.to" :to="item.to" @click="sidebarOpen = false">
            <component :is="item.icon" :size="18" /><span>{{ item.label }}</span>
          </RouterLink>
          <p class="nav-label">Trabajo</p>
          <RouterLink v-for="item in workNav" :key="item.to" :to="item.to" @click="sidebarOpen = false">
            <component :is="item.icon" :size="18" /><span>{{ item.label }}</span><em v-if="item.badge" class="danger-badge">{{ item.badge }}</em>
          </RouterLink>
          <p class="nav-label">Inteligencia</p>
          <RouterLink v-for="item in intelligenceNav" :key="item.to" :to="item.to" @click="sidebarOpen = false">
            <component :is="item.icon" :size="18" /><span>{{ item.label }}</span>
          </RouterLink>
          <p class="nav-label">Directorio</p>
          <RouterLink v-for="item in directoryNav" :key="item.to" :to="item.to" @click="sidebarOpen = false">
            <component :is="item.icon" :size="18" /><span>{{ item.label }}</span>
          </RouterLink>
        </template>
        <p class="nav-label">Canales</p>
        <RouterLink v-for="item in channelNav" :key="item.to" :to="item.to" @click="sidebarOpen = false">
          <component :is="item.icon" :size="18" /><span>{{ item.label }}</span>
        </RouterLink>
        <RouterLink v-for="item in administrator ? adminChannelNav : []" :key="item.to" :to="item.to" @click="sidebarOpen = false">
          <component :is="item.icon" :size="18" /><span>{{ item.label }}</span>
        </RouterLink>
      </nav>
      <RouterLink v-if="administrator" to="/system" class="system-link" @click="sidebarOpen = false"><Settings2 :size="18" /><span>Estado del sistema</span><i :class="{ live: connected }" /></RouterLink>
      <div class="profile-switcher">
        <Transition name="profile-menu">
          <div v-if="profileMenuOpen" class="profile-menu">
            <p>Sesión activa</p>
            <RouterLink v-if="activeDashboardUser?.personId" class="profile-session" :to="`/people/${encodeURIComponent(activeDashboardUser.personId)}`" aria-label="Abrir mi perfil" @click="profileMenuOpen=false">
              <ApiImage v-if="activeDashboardUser.photoUrl" :src="activeDashboardUser.photoUrl" :alt="`Foto de ${activeDashboardUser.name}`"/><span v-else>{{ activeDashboardUser.name.split(/\s+/).map(part => part[0]).join('').slice(0, 2).toUpperCase() }}</span>
              <span><strong>{{ activeDashboardUser.name }}</strong><small>{{ accessRoleName(activeDashboardUser.accessRole) }} · {{ activeDashboardUser.scopeType === 'all' ? 'Toda la empresa' : `${activeDashboardUser.accountCount} cuentas` }}</small></span>
            </RouterLink>
            <div v-else-if="activeDashboardUser" class="profile-session">
              <ApiImage v-if="activeDashboardUser.photoUrl" :src="activeDashboardUser.photoUrl" :alt="`Foto de ${activeDashboardUser.name}`"/><span v-else>{{ activeDashboardUser.name.split(/\s+/).map(part => part[0]).join('').slice(0, 2).toUpperCase() }}</span>
              <span><strong>{{ activeDashboardUser.name }}</strong><small>{{ accessRoleName(activeDashboardUser.accessRole) }} · {{ activeDashboardUser.scopeType === 'all' ? 'Toda la empresa' : `${activeDashboardUser.accountCount} cuentas` }}</small></span>
            </div>
            <button v-if="impersonationActive" class="restore-session-button" type="button" @click="returnToAdministrator"><Undo2 :size="15" /> Volver a mi sesión de administrador</button>
            <button class="logout-button" type="button" @click="logout"><LogOut :size="15" /> Cerrar sesión</button>
          </div>
        </Transition>
        <button class="sidebar-user" type="button" aria-label="Cambiar perfil" :aria-expanded="profileMenuOpen" @click="profileMenuOpen = !profileMenuOpen">
          <ApiImage v-if="activeDashboardUser?.photoUrl" :src="activeDashboardUser.photoUrl" :alt="`Foto de ${activeDashboardUser.name}`"/><span v-else>{{ activeUserInitials }}</span><span><strong>{{ activeDashboardUser?.name ?? 'Usuario' }}</strong><small>{{ accessRoleName(activeDashboardUser?.accessRole) }}</small></span><ChevronDown :size="15" />
        </button>
      </div>
    </aside>

    <section class="app-main">
      <header class="topbar">
        <button class="menu-button" aria-label="Abrir menú" @click="sidebarOpen = true"><Menu :size="21" /></button>
        <div class="topbar-title"><span>Centro operativo</span><strong>{{ pageTitle }}</strong></div>
        <button v-if="administrator" class="global-search" @click="openSearch"><Search :size="17" /><span>Buscar correos, personas, proyectos…</span><kbd>⌘ K</kbd></button>
        <div class="topbar-actions">
          <span class="live-pill"><i :class="{ connected: !administrator || connected }" />{{ !administrator ? 'Scope activo' : connected ? 'En vivo' : 'Conectando' }}</span>
          <button v-if="!debugMode" class="icon-button" aria-label="Notificaciones"><Bell :size="19" /><b /></button>
        </div>
      </header>
      <main class="page-stage"><RouterView /></main>
    </section>

    <Transition name="fade"><div v-if="toast" class="toast"><Sparkles :size="17" />{{ toast }}</div></Transition>
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="searchOpen" class="modal-wrap" @click.self="closeSearch">
          <section class="command-palette" role="dialog" aria-modal="true" aria-label="Búsqueda global">
            <div class="command-input"><Search :size="20" /><input ref="searchInput" v-model="searchQuery" placeholder="Busca una persona, asunto, cliente o proyecto…" /><kbd>ESC</kbd></div>
            <div class="command-results">
              <p v-if="searching" class="command-hint">Buscando en correo, análisis y entidades…</p>
              <p v-else-if="searchQuery.length < 2" class="command-hint">Escribe al menos dos caracteres. Prueba “Atlas” o “entrega”.</p>
              <button v-for="result in searchResults" :key="result.id" @click="goResult(result)">
                <span class="result-icon"><Inbox v-if="result.resultType === 'email' || result.senderEmail" :size="17" /><Lightbulb v-else :size="17" /></span>
                <span><strong>{{ result.title || result.name || result.subject }}</strong><small>{{ result.summary || result.preview || result.description }}</small></span>
                <em>Abrir</em>
              </button>
              <p v-if="searchQuery.length >= 2 && !searching && !searchResults.length" class="command-hint">No encontramos coincidencias.</p>
            </div>
          </section>
        </div>
      </Transition>
    </Teleport>
  </div>
  <div v-else class="auth-wrap">
    <form class="auth-card" @submit.prevent="loginStep === 'email' ? requestLoginCode() : loginStep === 'otp' ? verifyLoginCode() : verifyPassword()">
      <img v-if="companyLogoUrl" class="auth-logo" :src="companyLogoUrl" alt="Logo de Eqover" />
      <KeyRound v-if="loginStep !== 'email'" :size="23" aria-hidden="true" />
      <h1>{{ loginStep === 'email' ? 'Entra con tu correo' : loginStep === 'otp' ? 'Revisa tu correo' : 'Escribe tu contraseña' }}</h1>
      <p>{{ loginStep === 'email' ? 'Confirmaremos el método de acceso configurado para tu cuenta.' : loginStep === 'otp' ? `Escribe el código de 6 dígitos enviado a ${loginEmail}.` : `Usa la contraseña configurada para ${loginEmail}.` }}</p>
      <input v-if="loginStep === 'email'" v-model="loginEmail" type="email" autocomplete="email" inputmode="email" placeholder="tu@eqover.com.mx" autofocus required />
      <input v-else-if="loginStep === 'otp'" v-model="loginCode" class="auth-code-input auth-credential-input" type="text" autocomplete="one-time-code" inputmode="numeric" pattern="[0-9]{6}" maxlength="6" placeholder="000000" required />
      <input v-else v-model="loginPassword" class="auth-credential-input" type="password" autocomplete="current-password" minlength="8" maxlength="128" placeholder="Contraseña" required />
      <p v-if="authMessage" class="auth-feedback success">{{ authMessage }}</p>
      <p v-if="authError" class="auth-feedback error" role="alert">{{ authError }}</p>
      <button class="button primary" type="submit" :disabled="authBusy">{{ authBusy ? 'Procesando…' : loginStep === 'email' ? 'Continuar' : 'Entrar' }}</button>
      <button v-if="loginStep !== 'email'" class="auth-secondary" type="button" :disabled="authBusy" @click="resetLogin">Usar otro correo</button>
    </form>
  </div>
  <OfflineActionDialog />
  <NativeOtaUpdateDialog />
</template>

<style scoped>
.access-blank,.auth-wrap{position:fixed;inset:0;background:#fff}.access-blank{z-index:200}.auth-wrap{z-index:201;display:grid;place-items:center;padding:20px}.auth-card{width:min(410px,92vw);padding:30px;display:grid;justify-items:center;border:1px solid #eceef1;border-radius:14px;background:#fff;box-shadow:0 18px 55px rgba(5,10,25,.08)}.auth-logo{width:112px;height:112px;margin:-8px 0 10px;object-fit:contain}.auth-card>svg{margin-bottom:15px;color:#697185}.auth-card h1{margin:0;font-family:Georgia,serif;font-size:27px;font-weight:500}.auth-card>p:not(.eyebrow):not(.auth-feedback){margin:10px 0 17px;color:var(--muted);font-size:10px;line-height:1.55;text-align:center}.auth-card input{width:100%;height:44px;padding:0 13px;border:1px solid var(--line);border-radius:8px;outline:0;font-size:12px}.auth-card input:focus{border-color:var(--violet);box-shadow:0 0 0 3px rgba(115,87,216,.12)}.auth-card .auth-code-input{text-align:center;font-size:22px;font-weight:800;letter-spacing:8px}.auth-card .button{width:100%;margin-top:10px}.auth-card .button:disabled{opacity:.6;cursor:wait}.auth-feedback{width:100%;margin:10px 0 0;padding:9px 11px;border-radius:7px;font-size:9px;line-height:1.45;text-align:left}.auth-feedback.success{color:#28785a;background:#eaf7f0}.auth-feedback.error{color:#ad3541;background:#fff0f1}.auth-secondary{margin-top:12px;border:0;color:var(--violet);background:none;font-size:10px;font-weight:750;cursor:pointer}.profile-session{padding:9px 10px;display:grid;grid-template-columns:30px 1fr;align-items:center;gap:8px;color:inherit;text-decoration:none}.profile-session[href]{cursor:pointer;transition:background .16s}.profile-session[href]:hover,.profile-session[href]:focus-visible{background:#f7f5fd;outline:none}.profile-session>span:first-child{width:30px;height:30px;display:grid;place-items:center;border-radius:8px;color:#fff;background:var(--violet);font-size:9px;font-weight:850}.profile-session>span:last-child{display:grid}.profile-session strong{font-size:9px}.profile-session small{margin-top:2px;color:var(--muted);font-size:7px}.logout-button{width:100%;padding:9px 10px;display:flex;align-items:center;gap:7px;border:0;border-top:1px solid var(--line);color:var(--red);background:#fff;font-size:9px;font-weight:750;cursor:pointer}
.profile-session>.api-image{width:30px;height:30px;border-radius:8px}.restore-session-button{width:100%;padding:9px 10px;display:flex;align-items:center;gap:7px;border:0;border-top:1px solid #d9d0f4;color:#5c45b8;background:#f7f5fd;font-size:9px;font-weight:800;text-align:left;cursor:pointer}.sidebar-user>.api-image{width:32px;height:32px;border-radius:8px}
</style>
